import {Component, OnChanges, OnDestroy, OnInit} from '@angular/core';

import {QlService} from "../../../../../core/shared/ql/service/ql.service";
import {QlRequestDto} from "../../../../../core/shared/ql/data/dto/ql-request.dto";
import {QlQueryDto} from "../../../../../core/shared/ql/data/dto/ql-query.dto";
import {FilterCriteria} from "../../../../../core/shared/filter/api/filter.criteria";
import {Sorting} from "../../../../../core/shared/misc/sorting";
import {ObjectBaseComponent} from "../../../../../core/shared/object/mvs-object-base/object-base.component";
import {MvsCoreService} from "../../../../../core/shared/service/mvs-core.service";
import {MvsMessageService} from "../../../../../core/shared/service/message/mvs-message.service";
import {ConfirmationService} from "primeng/api";
import {ObserverService} from "../../../../../core/shared/object/service/observer.service";
import {
    BmContractPaymentPlannedEntry,
    BmContractPaymentPlannedInterface,
    BmContractPaymentPlannedRow,
    BmContractPaymentPlannedSuspendedMonth,
    BmContractPaymentPlannedSuspension,
    BmContractPaymentPlannedSuspensionQlRow
} from "./model/bm-contract-payment-planned-model.interface";

@Component({
    selector: 'bm-contract-payment-planned',
    templateUrl: './bm-contract-payment-planned.component.html',
    standalone: false
})
export class BmContractPaymentPlannedComponent extends ObjectBaseComponent
    implements OnInit, OnChanges, OnDestroy {

    paymentPlanned: BmContractPaymentPlannedInterface;

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService,
        protected qlService: QlService
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }


    override ngOnInit(): void {
        super.ngOnInit();
    }

    /**
     * Called when ObjectBase has loaded the object
     */
    override refreshComponent(): void {
        this.loadPaymentPlannedData();
    }

    /**
     * Load all data via QL (Contract + Account + Price + Planned Entries); the payment suspensions
     * follow in a second read ({@link loadSuspensions}).
     */
    private loadPaymentPlannedData(): void {

        this.busy = true;

        const contractQuery = this.getContractQuery();
        const paymentAccountQuery = this.getPaymentAccountQuery();
        const priceQuery = this.getPriceQuery();
        const plannedEntriesQuery = this.getPlannedEntriesQuery();

        const request: QlRequestDto = new QlRequestDto();
        request.queries = [
            contractQuery,
            paymentAccountQuery,
            priceQuery,
            plannedEntriesQuery
        ];

        request.pipes = request.queries.map(q => ({
            name: q.name,
            type: "view",
            simpleQuery: q.name,
            settings: {
                includeForm: true,
                includeCount: true
            }
        }));

        this.qlService.query(request).subscribe(response => {

          // CONTRACT
            const contract = response.pipeData["get.contract.detail"]?.data?.[0];
     console.log(response);

             // PAYMENT ACCOUNT
            const paymentAccount =
                response.pipeData["get.contract.payment.account"]?.data?.[0];

            // price
            const price =
                response.pipeData["get.contract.price.history"]?.data?.[0];

          // PLANNED ENTRIES
            const plannedEntries =
                response.pipeData["get.contract.payment.planned.entries"]?.data ?? [];

            const mappedEntries: BmContractPaymentPlannedEntry[] = plannedEntries.map(e => ({
                startDate: e.startDate,
                endDate: e.endDate,
                dueDate: e.dueDate,
                plannedTypeName: e.plannedTypeDtoName,
                status: e.status,
                amount: e.amount,
                remainingAmount: e.remainingAmount,
                currency: e.currency
            }));

            this.paymentPlanned = {
                contract: contract ? {
                    id: contract.id,
                    contractIdentifier: contract.contractIdentifier,
                    partnerName: contract.partnerDtoName,
                    productName: contract.productDtoName,
                    startDate: contract.startDate,
                    endDate: contract.endDate,
                    status: contract.baseStatusEnumName
                } : null,

                paymentAccounts: (response.pipeData["get.contract.payment.account"]?.data ?? []).map(pa => ({
                    billingAreaName: pa.billingAreaDtoName,
                    frequency: pa.frequencyEnumName,
                    currency: pa.currency,
                    paymentTiming: pa.paymentTiming,
                    paymentDayOfMonth: pa.paymentDayOfMonth,
                    paymentMethod: pa.paymentMethod,
                    // bm.CustomerBillingAccount no longer carries a sepaDirectDebitBankAccount FK
                    // (projects/billing_account_bank_timeslice, the bank is now a time slice,
                    // bm.CustomerBillingAccountBank) - this mock-fed query has no join producing a
                    // resolved FK name/nested Dto for it, so the mock payload now supplies the flat
                    // fields the interface already promises directly.
                    bankAccountName: pa.bankAccountName,
                    iban: pa.iban,
                    closed: pa.closed,
                    startDate: pa.startDate ?? null,
                    endDate: pa.endDate ?? null
                })),

                prices: (response.pipeData["get.contract.price.history"]?.data ?? []).map(p => ({
                    startDate: p.startDate,
                    priceFrequencyName: p.priceFrequencyDtoName,
                    priceGross: p.priceGross,
                    currency: p.currency
                })),

                plannedEntries: mappedEntries,

                // filled by loadSuspensions(); until then the list shows the entries only
                suspensions: [],

                rows: BmContractPaymentPlannedComponent.buildRows(mappedEntries, [])
            };

            this.busy = false;
            this.initialized = true;

            this.loadSuspensions();
        });
    }

    /**
     * Second read, built with the QL builder: the payment suspensions of the contract. Months inside
     * a suspension have no Sollstellung; once the answer is in, the rows of the Inkasso table mark them.
     *
     * Nothing is read without an object id: there would be no contract to filter by. If the read fails
     * (the builder already reports pipeline errors), the entries stay without markers.
     */
    private loadSuspensions(): void {

        const contractId = this.objectIdentifier?.objectId;

        if (contractId === null || contractId === undefined) {
            return;
        }

        const planned = this.paymentPlanned;
        const query = this.getSuspensionQuery(contractId);

        this.qlService.builder()
            .query(query)
            .execute()
            .subscribe({
                next: result => {
                    // a newer load replaced the view model meanwhile: this answer is outdated
                    if (this.paymentPlanned !== planned) {
                        return;
                    }

                    const suspensions = BmContractPaymentPlannedComponent.mapSuspensionRows(
                        result.get(query.name)
                    );

                    this.paymentPlanned = {
                        ...planned,
                        suspensions,
                        rows: BmContractPaymentPlannedComponent.buildRows(planned.plannedEntries, suspensions)
                    };
                },
                error: () => {
                    // entries stay without markers
                }
            });
    }


    private getContractQuery(): QlQueryDto {
        return {
            name: "get.contract.detail",
            type: "view",
            start: {
                type: "entity",
                name: "cm.Contract",
                as: "c"
            },
            fixedFilters: [
                FilterCriteria.create("c.id", "EQ", "${objectId}")
            ]
        };
    }

    private getPaymentAccountQuery(): QlQueryDto {
        return {
            name: "get.contract.payment.account",
            type: "view",
            start: {
                type: "entity",
                name: "cm.ContractPaymentAccount",
                as: "pa"
            },
            fixedFilters: [
                FilterCriteria.create("pa.contract.id", "EQ", "${objectId}")
            ]
        };
    }

    private getPriceQuery(): QlQueryDto {
        return {
            name: "get.contract.price.history",
            type: "view",
            start: {
                type: "entity",
                name: "cm.ContractPrice",
                as: "cp"
            },
            fixedFilters: [
                FilterCriteria.create("cp.contract.id", "EQ", "${objectId}")
            ]
        };
    }

    private getPlannedEntriesQuery(): QlQueryDto {
        return {
            name: "get.contract.payment.planned.entries",
            type: "view",
            start: {
                type: "entity",
                name: "cm.ContractPaymentPlanned",
                as: "pp"
            },
            fixedFilters: [
                FilterCriteria.create("pp.contract.id", "EQ", "${objectId}")
            ]
        };
    }

    /**
     * Payment suspensions of the contract (cm.ContractPaymentSuspension), oldest first; the reason
     * name comes from the `reason` join (r.name).
     */
    private getSuspensionQuery(contractId: number): QlQueryDto {
        return {
            name: 'get.contract.payment.suspensions',
            type: 'view',
            start: {
                type: 'entity',
                name: 'cm.ContractPaymentSuspension',
                as: 'cps',
                joins: [
                    {
                        type: 'entity',
                        name: 'reason',
                        as: 'r',
                        joinType: 'left'
                    }
                ]
            },
            fixedFilters: [
                FilterCriteria.create('cps.contractId', FilterCriteria.cOperatorEqual, contractId)
            ],
            sortings: [new Sorting('cps.startDate', true)]
        };
    }

    // ================= PAYMENT SUSPENSIONS: MONTH MARKING =================

    /**
     * Maps the QL rows of the suspension query (alias-prefixed keys: cps.startDate, r.name).
     */
    static mapSuspensionRows(
        rows: BmContractPaymentPlannedSuspensionQlRow[]
    ): BmContractPaymentPlannedSuspension[] {
        return (rows ?? []).map(row => ({
            startDate: row['cps.startDate'],
            endDate: row['cps.endDate'],
            reasonName: row['r.name'] ?? null
        }));
    }

    /**
     * Every calendar month inside a suspension (start month ... end month inclusive), ordered by
     * month, one entry per month.
     *
     * - Only the month of the dates counts, never the day.
     * - A month covered by two suspensions (the backend forbids overlaps) keeps the one that starts earlier.
     * - A suspension without a readable start or end, or with the end before the start, marks nothing.
     */
    static buildSuspendedMonths(
        suspensions: BmContractPaymentPlannedSuspension[]
    ): BmContractPaymentPlannedSuspendedMonth[] {
        return BmContractPaymentPlannedComponent.toSuspendedMonths(
            BmContractPaymentPlannedComponent.monthMarks(suspensions)
        );
    }

    /**
     * Rows of the Inkasso table, ordered by date (stable): the planned entries plus one marker row
     * for every suspended month. A marker row precedes the entries of the same date. An entry whose
     * due date lies in a suspended month carries that suspension as well.
     */
    static buildRows(
        entries: BmContractPaymentPlannedEntry[],
        suspensions: BmContractPaymentPlannedSuspension[]
    ): BmContractPaymentPlannedRow[] {

        const marks = BmContractPaymentPlannedComponent.monthMarks(suspensions);

        const suspendedMonthRows = BmContractPaymentPlannedComponent.toSuspendedMonths(marks)
            .map((month): BmContractPaymentPlannedRow => ({
                kind: 'suspendedMonth',
                date: month.monthStart,
                entry: null,
                suspension: month.suspension
            }));

        const entryRows = (entries ?? [])
            .map((entry): BmContractPaymentPlannedRow => {
                const monthIndex = BmContractPaymentPlannedComponent.monthIndexOf(entry.dueDate);
                return {
                    kind: 'entry',
                    date: entry.dueDate,
                    entry,
                    suspension: monthIndex === null ? null : (marks.get(monthIndex) ?? null)
                };
            });

        return [...suspendedMonthRows, ...entryRows].sort((a, b) =>
            BmContractPaymentPlannedComponent.compareIsoDates(a.date, b.date)
        );
    }

    /**
     * Month index (year * 12 + month - 1) -> the suspension covering that month.
     */
    private static monthMarks(
        suspensions: BmContractPaymentPlannedSuspension[]
    ): Map<number, BmContractPaymentPlannedSuspension> {

        const marks = new Map<number, BmContractPaymentPlannedSuspension>();

        const byStartDate = [...(suspensions ?? [])].sort((a, b) =>
            BmContractPaymentPlannedComponent.compareIsoDates(a.startDate, b.startDate)
        );

        for (const suspension of byStartDate) {
            const firstMonth = BmContractPaymentPlannedComponent.monthIndexOf(suspension.startDate);
            const lastMonth = BmContractPaymentPlannedComponent.monthIndexOf(suspension.endDate);

            if (firstMonth === null || lastMonth === null || lastMonth < firstMonth) {
                continue;
            }

            for (let month = firstMonth; month <= lastMonth; month++) {
                if (!marks.has(month)) {
                    marks.set(month, suspension);
                }
            }
        }

        return marks;
    }

    private static toSuspendedMonths(
        marks: Map<number, BmContractPaymentPlannedSuspension>
    ): BmContractPaymentPlannedSuspendedMonth[] {
        return Array.from(marks.keys())
            .sort((a, b) => a - b)
            .map(monthIndex => ({
                monthStart: BmContractPaymentPlannedComponent.monthStartOf(monthIndex),
                suspension: marks.get(monthIndex)
            }));
    }

    /**
     * Month index of an ISO date (YYYY-MM-DD...), or null when it is not readable. Reads the string
     * directly: no Date object, so no time zone can move a date into the neighbouring month.
     */
    private static monthIndexOf(isoDate: string | null | undefined): number | null {
        const match = /^(\d{4})-(\d{2})/.exec(isoDate ?? '');
        if (!match) {
            return null;
        }
        const month = Number(match[2]);
        return month >= 1 && month <= 12 ? Number(match[1]) * 12 + month - 1 : null;
    }

    /**
     * First day (YYYY-MM-01) of the month with the given month index.
     */
    private static monthStartOf(monthIndex: number): string {
        const year = Math.floor(monthIndex / 12);
        const month = (monthIndex % 12) + 1;
        return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-01`;
    }

    private static compareIsoDates(a: string | null | undefined, b: string | null | undefined): number {
        const left = a ?? '';
        const right = b ?? '';
        return left < right ? -1 : left > right ? 1 : 0;
    }

    selectedPaymentAccountIndex = 0;
    selectedPriceIndex = 0;

    showAllPaymentAccounts = false;
    showPriceHistory = false;

    togglePaymentAccounts(): void {
        this.showAllPaymentAccounts = !this.showAllPaymentAccounts;
    }

    togglePriceHistory(): void {
        this.showPriceHistory = !this.showPriceHistory;
    }

    selectPaymentAccount(index: number): void {
        this.selectedPaymentAccountIndex = index;
        this.showAllPaymentAccounts = false;
    }

    selectPrice(index: number): void {
        this.selectedPriceIndex = index;
        this.showPriceHistory = false;
    }
}
